"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { agenticWorkflow } from "@/lib/data/site";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const steps = agenticWorkflow.map((label, index) => ({
  label,
  detail: [
    "A business request enters the plane — chat, ticket, API, or workflow trigger.",
    "The orchestrator plans the path, picks agents, and applies policy constraints.",
    "Specialized agents take ownership of discrete workstreams in parallel.",
    "Tools execute — search, compute, transform, generate, and validate.",
    "Enterprise data grounds every decision in current, trusted context.",
    "CRM, ERP, and apps become actionable surfaces — not just data sources.",
    "The system proposes or selects the next best business outcome.",
    "Action ships under audit trails, approvals, and guardrails.",
  ][index],
}));

/** Node positions around a control-plane ring (viewBox 560×460) */
const planeNodes = [
  { x: 280, y: 70, r: 0 },
  { x: 410, y: 120, r: 1 },
  { x: 450, y: 230, r: 2 },
  { x: 410, y: 340, r: 3 },
  { x: 280, y: 390, r: 4 },
  { x: 150, y: 340, r: 5 },
  { x: 110, y: 230, r: 6 },
  { x: 150, y: 120, r: 7 },
] as const;

export function AgenticShowcase() {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const current = steps[active];

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % steps.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, [reduced]);

  return (
    <section className="relative overflow-hidden py-20 sm:py-24 md:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,var(--hero-glow-1),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_10%_80%,var(--hero-glow-2),transparent_50%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border-strong to-transparent" />

      <div className="container-wide relative">
        <div className="grid items-center gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10 xl:gap-16">
          {/* Copy + active step story */}
          <div className="relative z-10 max-w-xl">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="eyebrow mb-5"
            >
              Agentic AI
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65 }}
              className="display-title text-[clamp(2rem,5.5vw,3.6rem)]"
            >
              A CONTROL PLANE
              <br />
              <span className="gradient-text">FOR ENTERPRISE ACTION</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.55 }}
              className="mt-5 max-w-md text-base leading-relaxed text-muted sm:text-lg"
            >
              Watch intent move through orchestrators, agents, tools, and business systems — until
              work is done, not just described.
            </motion.p>

            <div className="mt-9 min-h-[9.5rem]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.label}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-accent">
                      {String(active + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-accent/50 to-transparent" />
                  </div>
                  <h3 className="mt-3 font-display text-2xl tracking-tight text-foreground sm:text-3xl">
                    {current.label}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted sm:text-[15px]">
                    {current.detail}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Step selector — compact rail, not cards */}
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Agentic workflow steps">
              {steps.map((step, index) => (
                <button
                  key={step.label}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  onClick={() => setActive(index)}
                  className={cn(
                    "h-2.5 rounded-full transition-all duration-300",
                    index === active
                      ? "w-9 bg-gradient-to-r from-accent to-cyan shadow-[0_0_16px_var(--accent-glow)]"
                      : "w-2.5 bg-border-strong hover:bg-accent/50",
                  )}
                  aria-label={step.label}
                />
              ))}
            </div>

            <div className="mt-9">
              <Button href="/services/artificial-intelligence" size="md" variant="outline" arrow cursorLabel="AI">
                Explore AI engineering
              </Button>
            </div>
          </div>

          {/* Visual control plane */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto aspect-[560/460] w-full max-w-[38rem] lg:max-w-none"
          >
            <div className="pointer-events-none absolute inset-[12%] rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--accent)_22%,transparent),transparent_68%)] blur-3xl" />
            <div className="pointer-events-none absolute inset-[22%] rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--cyan)_18%,transparent),transparent_70%)] blur-2xl" />

            <ControlPlaneGraphic active={active} reduced={Boolean(reduced)} onSelect={setActive} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ControlPlaneGraphic({
  active,
  reduced,
  onSelect,
}: {
  active: number;
  reduced: boolean;
  onSelect: (index: number) => void;
}) {
  const core = planeNodes[active];

  return (
    <svg viewBox="0 0 560 460" className="relative h-full w-full drop-shadow-[0_24px_60px_rgba(11,40,90,0.12)]" aria-hidden>
      <defs>
        <linearGradient id="ap-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.85" />
          <stop offset="55%" stopColor="var(--violet)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="ap-spoke" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.15" />
          <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0.7" />
        </linearGradient>
        <radialGradient id="ap-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--cyan)" stopOpacity="0.45" />
          <stop offset="55%" stopColor="var(--accent)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
        <filter id="ap-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Soft stage */}
      <ellipse cx="280" cy="230" rx="210" ry="175" fill="url(#ap-core)" opacity="0.9" />

      {/* Orbit rings */}
      <motion.circle
        cx="280"
        cy="230"
        r="168"
        fill="none"
        stroke="url(#ap-ring)"
        strokeWidth="1.25"
        strokeDasharray="3 8"
        animate={reduced ? undefined : { rotate: 360 }}
        transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "280px 230px" }}
        opacity="0.55"
      />
      <motion.circle
        cx="280"
        cy="230"
        r="118"
        fill="none"
        stroke="color-mix(in srgb, var(--accent) 35%, transparent)"
        strokeWidth="1"
        animate={reduced ? undefined : { rotate: -360 }}
        transition={{ duration: 42, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "280px 230px" }}
      />
      <circle
        cx="280"
        cy="230"
        r="72"
        fill="color-mix(in srgb, var(--card) 72%, transparent)"
        stroke="color-mix(in srgb, var(--accent) 28%, transparent)"
        strokeWidth="1.25"
      />

      {/* Spokes from core to nodes */}
      {planeNodes.map((node, i) => (
        <motion.line
          key={`spoke-${node.r}`}
          x1="280"
          y1="230"
          x2={node.x}
          y2={node.y}
          stroke="url(#ap-spoke)"
          strokeWidth={i === active ? 2.2 : 1}
          strokeLinecap="round"
          animate={{ opacity: i === active ? 1 : 0.28 }}
          transition={{ duration: 0.35 }}
        />
      ))}

      {/* Traveling pulse along active spoke */}
      {!reduced ? (
        <motion.circle
          key={`pulse-${active}`}
          r="4.5"
          fill="var(--cyan)"
          filter="url(#ap-glow)"
          initial={{ cx: 280, cy: 230, opacity: 0 }}
          animate={{
            cx: [280, core.x],
            cy: [230, core.y],
            opacity: [0, 1, 1, 0],
          }}
          transition={{ duration: 1.35, ease: "easeInOut", repeat: Infinity, repeatDelay: 0.55 }}
        />
      ) : null}

      {/* Center hub */}
      <motion.g
        animate={reduced ? undefined : { scale: [1, 1.04, 1] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "280px 230px" }}
      >
        <circle cx="280" cy="230" r="38" fill="var(--accent)" filter="url(#ap-glow)" opacity="0.95" />
        <circle cx="280" cy="230" r="28" fill="color-mix(in srgb, var(--background) 20%, var(--accent))" />
        <text
          x="280"
          y="226"
          textAnchor="middle"
          fill="#fff"
          fontSize="9"
          fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
          letterSpacing="0.14em"
        >
          ORCH
        </text>
        <text
          x="280"
          y="240"
          textAnchor="middle"
          fill="rgba(255,255,255,0.75)"
          fontSize="8"
          fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
          letterSpacing="0.08em"
        >
          CORE
        </text>
      </motion.g>

      {/* Outer workflow nodes */}
      {planeNodes.map((node, i) => {
        const isActive = i === active;
        const label = steps[i].label;
        const short = ["Request", "Orch", "Agent", "Tools", "Data", "Systems", "Decision", "Action"][i];
        const dx = node.x - 280;
        const dy = node.y - 230;
        const len = Math.hypot(dx, dy) || 1;
        const lx = node.x + (dx / len) * 36;
        const ly = node.y + (dy / len) * 36;

        return (
          <g key={label} className="cursor-pointer" onClick={() => onSelect(i)}>
            <title>{label}</title>
            <motion.circle
              cx={node.x}
              cy={node.y}
              r={isActive ? 26 : 20}
              fill={isActive ? "var(--accent)" : "var(--card)"}
              stroke={isActive ? "var(--cyan)" : "color-mix(in srgb, var(--border-strong) 90%, transparent)"}
              strokeWidth={isActive ? 2 : 1.25}
              filter={isActive ? "url(#ap-glow)" : undefined}
              animate={{ r: isActive ? 26 : 20 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
            />
            {isActive && !reduced ? (
              <motion.circle
                cx={node.x}
                cy={node.y}
                r="32"
                fill="none"
                stroke="var(--cyan)"
                strokeWidth="1"
                initial={{ opacity: 0.65, scale: 0.9 }}
                animate={{ opacity: 0, scale: 1.3 }}
                transition={{ duration: 1.35, repeat: Infinity }}
                style={{ transformOrigin: `${node.x}px ${node.y}px` }}
              />
            ) : null}
            <text
              x={node.x}
              y={node.y + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isActive ? "#fff" : "var(--accent)"}
              fontSize="10"
              fontWeight="600"
              fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
            >
              {String(i + 1).padStart(2, "0")}
            </text>
            <motion.text
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isActive ? "var(--foreground)" : "var(--muted)"}
              fontSize="11"
              fontWeight="600"
              fontFamily="var(--font-maxridge-display), system-ui, sans-serif"
              animate={{ opacity: isActive ? 1 : 0.72 }}
            >
              {short}
            </motion.text>
          </g>
        );
      })}
    </svg>
  );
}
